import ContentApprovalStatusChip from "@/components/Campaign/management/activeTab/ContentApprovalStatusChip";
import CarouselAlbumItem from "@/components/Campaign/management/components/carousel/CarouselAlbumItem";
import CarouselContentStatistics from "@/components/Campaign/management/components/carousel/CarouselContentStatistics";
import CarouselFooter from "@/components/Campaign/management/components/carousel/footer/CarouselFooter";
import ContentLikeButton from "@/components/Campaign/management/components/ContentLikeButton";
import Loader from "@/components/Common/kpi/Loader";
import ContentGuidanceBox from "@/components/ContentApproval/ContentGuidanceBox";
import {useSourceAlbum} from "@/hooks/useSourceAlbum";
import clsxm from "@/lib/clsxm";
import useCampaignManagementStore from "@/store/campaignManagement";
import {useCarouselStore, type TCarouselMedia} from "@/store/carousel";
import {shouldMuteVideosAtom} from "@/store/jotaiAtoms";
import type {TInstagramMedia} from "@/store/types";
import {useAtom} from "jotai";
import {useEffect, useRef, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useEvent, useMeasure} from "react-use";
import {useStateWithDeps} from "use-state-with-deps";

const CarouselItem: FC<{
  content: TCarouselMedia;
  isCurrentlyVisible: boolean;
}> = ({content: rawContent, isCurrentlyVisible}) => {
  const {t} = useTranslation("content_approval");
  const content = useSourceAlbum({content: rawContent}) ?? rawContent;
  const [shouldMuteVideos, setShouldMuteVideos] = useAtom(shouldMuteVideosAtom);
  const defaultAlbumItem = useCarouselStore(state => state.defaultAlbumItem);
  const isGalleryTab = useCampaignManagementStore(state => state.tabState === "gallery");
  const showCaption = useCarouselStore(state => state.showCaption);
  const showFooter = useCarouselStore(state => state.showFooter);
  const staticFooter = useCarouselStore(state => state.staticFooter);
  const variant = useCarouselStore(state => state.variant);
  const media = (content.instagram_media?.media_file ?? content.instagram_media?.media_url)!;
  const thumbnail = (content.instagram_media?.thumbnail_file ?? content.instagram_media?.thumbnail_url)!;

  const videoRef = useRef<HTMLVideoElement>(null!);
  const setCarouselValue = useCarouselStore(state => state.setValue);
  const [contentSectionRef, {height: contentSectionHeight, width: contentSectionWidth}] = useMeasure<HTMLDivElement>();
  const [isVideoHovered, setIsVideoHovered] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const [hoveredAlbumItem, setHoveredAlbumItem] = useStateWithDeps<TInstagramMedia | null>(
    defaultAlbumItem ?? content.instagram_media?.children?.[0] ?? null,
    [defaultAlbumItem],
  );

  const canShowAlbumItem = hoveredAlbumItem && isCurrentlyVisible;
  const mediaData = content.instagram_media;
  const hasMoreChildren = (content.instagram_media?.children?.length ?? 0) > 1;
  const isVideoContent = canShowAlbumItem
    ? hoveredAlbumItem?.media_type === "VIDEO"
    : mediaData?.media_type === "VIDEO";

  useEffect(() => {
    if (contentSectionWidth > 0 && isCurrentlyVisible) {
      setCarouselValue("contentSectionWidth", contentSectionWidth);
    }
  }, [contentSectionWidth, isCurrentlyVisible]);

  const renderMedia = () => {
    if (canShowAlbumItem) {
      return <CarouselAlbumItem content={hoveredAlbumItem} setHoveredAlbumItem={setHoveredAlbumItem} />;
    }

    if (mediaData?.media_type === "PDF" && media) {
      return <iframe src={media} className="h-[45vh] w-full max-w-full sm:h-[70vh]" />;
    }

    if (mediaData?.media_type === "IMAGE" || !mediaData || !media) {
      return (
        <img src={media ?? thumbnail} className="max-h-[45vh] max-w-full rounded-xl object-contain sm:max-h-[60vh]" />
      );
    }

    if (mediaData?.media_type === "VIDEO") {
      const tiktokVideoIdMatch = media?.match(/tiktok\.com\/(?:.*\/video\/|embed\/v2\/|embed\/)(\d+)/i);

      if (tiktokVideoIdMatch) {
        return (
          <div className="isolate h-[min(792px,50vh)] w-full max-w-full overflow-hidden rounded-xl bg-black sm:h-[min(792px,70vh)] sm:w-81.25">
            {isCurrentlyVisible ? (
              <iframe
                src={`https://www.tiktok.com/embed/v2/${tiktokVideoIdMatch[1]}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="block size-full border-none"
              />
            ) : (
              thumbnail && <img src={thumbnail} alt="" className="size-full object-cover" />
            )}
          </div>
        );
      }

      return (
        <div className="relative flex justify-center">
          {!isVideoReady && (
            <div className="relative">
              {thumbnail ? (
                <img src={thumbnail} className="max-h-[45vh] max-w-full rounded-xl object-contain sm:max-h-[60vh]" />
              ) : (
                <div className="aspect-square max-h-[45vh] w-full max-w-100 bg-black sm:max-h-[60vh]" />
              )}
              <Loader
                styles={{containerClassName: "absolute inset-0 items-center", loaderClassName: "size-10 text-white"}}
              />
            </div>
          )}
          <video
            ref={videoRef}
            preload="none"
            className={clsxm(
              "safari-wide-controls max-h-[45vh] max-w-full rounded-xl object-contain opacity-0 transition-opacity duration-150 sm:max-h-[60vh]",
              isVideoReady ? "opacity-100" : "absolute inset-0 size-full",
            )}
            controls
            controlsList="nodownload"
            loop
            muted={shouldMuteVideos}
            onCanPlay={() => setIsVideoReady(true)}
            onVolumeChange={e => setShouldMuteVideos(e.currentTarget.muted || e.currentTarget.volume === 0)}
            src={media}
          />
        </div>
      );
    }

    return null;
  };

  const renderOverlays = () => (
    <>
      {isGalleryTab && <ContentLikeButton content={content} />}
      {((variant === "content_approval" && content.submissionStatus) || content.submissionStatus === "REJECTED") &&
        !isVideoHovered && (
          <div className="absolute top-5 left-5">
            <ContentApprovalStatusChip status={content.submissionStatus} />
          </div>
        )}
      {variant === "content_approval" && content.submissionStatus === "PUBLISHED" && (
        <ContentLikeButton className="top-5 right-5 translate-x-0 translate-y-0" content={content} />
      )}
    </>
  );

  useEffect(() => {
    // react-slick + adaptiveHeight does not recalculate size correctly on content changes
    // Triggering a window resize is a workaround to force slick to re-measure and update size.
    // contentSectionHeight covers anything that resizes the slide from the inside - expanding a
    // caption, for one - which slick would otherwise clip behind its fixed slick-list height.
    window.dispatchEvent(new Event("resize"));
  }, [contentSectionHeight, hoveredAlbumItem, isVideoReady]);

  useEffect(() => {
    if (!videoRef.current) return;

    // react-slick clones slides for infinite scroll — cloned videos must never play
    const isClonedSlide = videoRef.current.closest(".slick-cloned") !== null;

    if (isCurrentlyVisible && !isClonedSlide) {
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isCurrentlyVisible]);

  useEvent("blur-sm", () => setHoveredAlbumItem(null));

  return (
    <div className={`flex max-w-full grow flex-col overflow-hidden ${content.caption ? "mx-2.5 sm:mx-auto" : ""}`}>
      <div
        className={clsxm("flex h-screen flex-col items-center justify-center", staticFooter && "h-fit")}
        onContextMenu={e => e.preventDefault()}>
        <div
          ref={contentSectionRef}
          className="flex w-fit max-w-full flex-col items-center gap-2.5 self-center"
          onClick={e => e.stopPropagation()}>
          <div className="flex flex-col justify-center gap-2.5 sm:gap-7 lg:flex-row">
            <div className="relative flex w-full flex-col items-center">
              <div
                className="relative flex items-center justify-center"
                onMouseEnter={() => isVideoContent && setIsVideoHovered(true)}
                onMouseLeave={() => setIsVideoHovered(false)}>
                {renderMedia()}
                {renderOverlays()}
              </div>
            </div>
            {!!content.caption && showCaption && (
              <ContentGuidanceBox
                className="max-h-[45vh] max-w-100 gap-3 rounded-xl border border-none bg-black p-2.5 select-text sm:p-5 lg:max-h-[60vh] lg:min-w-75"
                date={content.instagram_media?.timestamp}
                description={content.caption}
                descriptionClassName="bg-black rounded-b-none p-0 text-white "
                descriptionTextClassName="text-white text-sm"
                expandableButtonClassName="text-white text-2xs font-semibold"
                influencerImage={content.user?.picture}
                title={t("caption")}
                titleClassName="min-h-6 bg-neutral-black p-0 bg-black rounded-t-xl border-none min-h-fit  text-white"
              />
            )}
          </div>
          {(variant === "default" ||
            variant === "gallery" ||
            variant === "rating" ||
            variant === "social_feed" ||
            content.submissionStatus === "PUBLISHED") && (
            <CarouselContentStatistics className="px-1.5 sm:px-5" content={content} />
          )}
        </div>
        <div className="flex flex-col self-stretch">
          {hasMoreChildren && (
            <div
              className="mt-4 flex max-w-full flex-wrap items-center justify-center gap-4 pb-0.5"
              onClick={e => e.stopPropagation()}>
              {content.instagram_media?.children?.map((child, index) => (
                <CarouselAlbumItem
                  key={index}
                  className={clsxm(hoveredAlbumItem?.id === child.id && "ring-primary-dark-solid ring-2")}
                  content={child}
                  setHoveredAlbumItem={setHoveredAlbumItem}
                  isHoverable
                />
              ))}
            </div>
          )}
          {showFooter && !staticFooter && (
            <div className="mt-2.5 flex justify-center self-center">
              <CarouselFooter content={content} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarouselItem;
